import React, { useEffect, useState } from "react"; 
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { FaEdit, FaSave, FaDownload } from "react-icons/fa";
import "../styles/Jobpreviewpage.css";

const JobPreviewPage = () => {
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);

  // ✅ Helper: extract readable text from PDF-like content
  const extractPdfText = (raw) => {
    if (!raw) return "";
    const matches = [...raw.matchAll(/\(([^)]*)\)/g)];
    if (matches.length === 0) return raw; // if it's normal text, just show it
    return matches.map((m) => m[1]).join(" ");
  };

  // ✅ Load job data from localStorage (support multiple keys)
  useEffect(() => {
    let savedData = null;

    const keysToTry = ["jobFormData", "viewJob", "jobPreviewData"];
    for (const key of keysToTry) {
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          savedData = JSON.parse(raw);
          break;
        } catch (e) {
          console.error(`Invalid JSON in ${key}`, e);
        }
      }
    }

    if (savedData) {
      setJobData(savedData);
    }
  }, []);

  // ✅ Edit → navigates back with saved data
  const handleEdit = () => {
    if (jobData) {
      localStorage.setItem("editJobFormData", JSON.stringify(jobData));
      localStorage.setItem("skipStartModal", "true");
      navigate("/post-job", { state: { editMode: true } });
    }
  };

  // ✅ Save draft locally
  const handleSaveDraft = () => {
    if (jobData) {
      localStorage.setItem("jobDraftData", JSON.stringify(jobData));
      alert("💾 Draft saved successfully!");
    }
  };

  // ✅ Download job details as PDF
  const handleDownloadPDF = () => {
    if (!jobData) return;
    const doc = new jsPDF("p", "pt", "a4");
    const margin = 40;
    let y = 60;

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(18);
    doc.text(jobData.jobTitle || "Job Preview", margin, y);
    y += 20;

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(12);

    const addSection = (title, value) => {
      doc.setFont("Helvetica", "bold");
      doc.text(title, margin, y);
      y += 15;
      doc.setFont("Helvetica", "normal");

      const lines = doc.splitTextToSize(value || "-", 500);
      doc.text(lines, margin, y);
      y += lines.length * 14 + 10;
    };

    addSection("Job Type:", jobData.jobType);
    addSection("Category:", jobData.category);
    addSection("Location:", jobData.location);
    addSection("Description:", extractPdfText(jobData.description));
    addSection("Requirements:", extractPdfText(jobData.requirements));
    addSection("Salary Range:", jobData.salaryRange);
    addSection("Experience Required:", jobData.experience);
    addSection("Qualifications:", jobData.qualifications);
    addSection("Skills:", (jobData.skills || []).join(", "));
    addSection("Number of Openings:", jobData.openings);
    addSection("Application Deadline:", jobData.deadline);
    addSection("Contact Email:", jobData.contactEmail);

    doc.save(`${jobData.jobTitle || "Job_Preview"}.pdf`);
  };

  // ✅ Publish
  const handlePublish = () => {
    alert("✅ Job published successfully!");
    localStorage.removeItem("jobFormData");
    localStorage.removeItem("jobDraftData");
    localStorage.removeItem("skipStartModal");
    localStorage.removeItem("editJobFormData");

    navigate("/recruiter-dashboard");
  };

  if (!jobData) {
    return (
      <div className="preview-container">
        <p className="loading-text">No job details available for preview.</p>
      </div>
    );
  }

  return (
    <div className="preview-container">
      <div className="preview-header">
        <h2 className="preview-title">Job Preview</h2>
        <div className="preview-actions">
          <FaEdit
            className="preview-icon edit"
            title="Edit Job"
            onClick={handleEdit}
          />
          <FaSave
            className="preview-icon save"
            title="Save as Draft"
            onClick={handleSaveDraft}
          />
          <FaDownload
            className="preview-icon download"
            title="Download PDF"
            onClick={handleDownloadPDF}
          />
        </div>
      </div>

      <div className="preview-content">
        <div className="preview-section">
          <h3>{jobData.jobTitle}</h3>
          <p>
            <strong>Type:</strong> {jobData.jobType} |{" "}
            <strong>Category:</strong> {jobData.category} |{" "}
            <strong>Location:</strong> {jobData.location}
          </p>
        </div>

        <div className="preview-section">
          <h4>Description</h4>
          <p>{extractPdfText(jobData.description)}</p>
        </div>

        <div className="preview-section">
          <h4>Requirements</h4>
          <p>{extractPdfText(jobData.requirements)}</p>
        </div>

        <div className="preview-section">
          <h4>Additional Information</h4>
          <p>
            <strong>Salary Range:</strong> {jobData.salaryRange}
          </p>
          <p>
            <strong>Experience Required:</strong> {jobData.experience}
          </p>
          <p>
            <strong>Qualifications:</strong> {jobData.qualifications}
          </p>
        </div>

        <div className="preview-section">
          <h4>Skills</h4>
          <div className="skills-preview">
            {(jobData.skills || []).map((skill, i) => (
              <span key={i} className="skill-chip">
                {skill}
              </span>
            ))}
          </div>
        </div>

        <div className="preview-section">
          <h4>Job Details</h4>
          <p>
            <strong>Openings:</strong> {jobData.openings}
          </p>
          <p>
            <strong>Deadline:</strong> {jobData.deadline}
          </p>
          <p>
            <strong>Contact Email:</strong> {jobData.contactEmail}
          </p>
        </div>

        <div className="preview-footer">
          <button className="publish-btn" onClick={handlePublish}>
            Publish Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobPreviewPage;
